import { useState, useEffect, useCallback } from 'react'
import { Shield, LogOut, Plus, Copy, Check, Download, Users, ChevronDown, ChevronUp, RefreshCw, Trash2, Edit2, X, RotateCcw } from 'lucide-react'
import { generateAccessCode } from '../calculs'
import { createAccessCode, listAccessCodes, deleteAccessCode, updateProspectCode, storageMode } from '../supabase'
import type { AccessCode } from '../types'

interface AdminDashboardProps { onLogout: () => void }
interface ProspectForm { prenom: string; nom: string; salon: string; email: string; telephone: string; adresse: string }
const EMPTY: ProspectForm = { prenom:'', nom:'', salon:'', email:'', telephone:'', adresse:'' }

export default function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const [form, setForm]               = useState<ProspectForm>(EMPTY)
  const [generatedCode, setGenCode]   = useState('')
  const [copied, setCopied]           = useState(false)
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState('')
  const [success, setSuccess]         = useState('')
  const [prospects, setProspects]     = useState<AccessCode[]>([])
  const [loadingP, setLoadingP]       = useState(false)
  const [expandCreate, setExpand]     = useState(true)
  const [editingId, setEditingId]     = useState<string|null>(null)
  const [editForm, setEditForm]       = useState<Partial<ProspectForm & { code: string }>>({})
  const [editLoading, setEditLoading] = useState(false)

  const load = useCallback(async () => {
    setLoadingP(true)
    const { data } = await listAccessCodes()
    if (data) setProspects(data)
    setLoadingP(false)
  }, [])

  useEffect(() => { load() }, [load])

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setSuccess('')
    if (!form.prenom || !form.nom || !form.salon || !form.email) {
      setError('Prénom, Nom, Salon et Email sont obligatoires.'); return
    }
    setLoading(true)
    const code = generateAccessCode()
    const result = await createAccessCode({ code, ...form })
    setLoading(false)
    if (result.success) { setGenCode(code); setSuccess('Code créé !'); setForm(EMPTY); load() }
    else setError(result.error ?? 'Erreur.')
  }

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code)
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }

  const handleDelete = async (p: AccessCode) => {
    if (!confirm(`Supprimer ${p.prenom} ${p.nom} (${p.code}) ?`)) return
    const result = await deleteAccessCode(p.id)
    if (result.success) { setSuccess(`${p.prenom} supprimé.`); load() }
    else setError(result.error ?? 'Erreur.')
  }

  const startEdit = (p: AccessCode) => {
    setEditingId(p.id)
    setEditForm({ prenom: p.prenom, nom: p.nom, salon: p.salon, email: p.email, telephone: p.telephone ?? '', code: p.code })
  }

  const handleRegenCode = () => setEditForm(f => ({ ...f, code: generateAccessCode() }))

  const handleSaveEdit = async () => {
    if (!editingId) return
    setEditLoading(true)
    const result = await updateProspectCode(editingId, editForm.code ?? '', {
      prenom: editForm.prenom, nom: editForm.nom,
      salon: editForm.salon, email: editForm.email, telephone: editForm.telephone,
    })
    setEditLoading(false)
    if (result.success) { setSuccess('Mis à jour !'); setEditingId(null); load() }
    else setError(result.error ?? 'Erreur.')
  }

  const exportCSV = () => {
    const h = ['Prénom','Nom','Salon','Email','Téléphone','Adresse','Code','Créé le','Statut']
    const rows = prospects.map(p => [
      p.prenom, p.nom, p.salon, p.email, p.telephone??'', p.adresse??'', p.code,
      new Date(p.created_at).toLocaleDateString('fr-FR'),
      p.first_login_at ? 'Actif' : 'Jamais connecté'
    ])
    const csv = [h,...rows].map(r => r.join(';')).join('\n')
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'})
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
    a.download = `prospects_${new Date().toISOString().split('T')[0]}.csv`; a.click()
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>
      <header style={{ background: '#0D1B2A' }} className="sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-white/70" />
            <span className="font-bold text-white">Admin — Loris Hug Innovation</span>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 text-white/70 hover:text-white text-sm">
            <LogOut className="w-4 h-4" /> Se déconnecter
          </button>
        </div>
      </header>

      {storageMode === 'local' && (
        <div className="max-w-6xl mx-auto px-6 pt-4">
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl text-sm"
            style={{ background:'#FFFBEB', border:'1px solid #FDE68A', color:'#92400E' }}>
            <span>⚠️</span>
            <span><strong>Mode local</strong> — données stockées dans ce navigateur uniquement.</span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">

        {/* Créer */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <button onClick={() => setExpand(!expandCreate)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5" style={{ color:'#1A3C6E' }} />
              <span className="font-semibold text-gray-800">Créer un nouveau code d'accès</span>
            </div>
            {expandCreate ? <ChevronUp className="w-4 h-4 text-gray-400"/> : <ChevronDown className="w-4 h-4 text-gray-400"/>}
          </button>

          {expandCreate && (
            <div className="px-6 pb-6 border-t border-gray-50">
              <form onSubmit={handleGenerate} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[{key:'prenom',label:'Prénom *',ph:'Jean'},{key:'nom',label:'Nom *',ph:'Dupont'},{key:'salon',label:'Salon *',ph:'Studio Éclat'},{key:'email',label:'Email *',ph:'jean@salon.fr',type:'email'},{key:'telephone',label:'Téléphone',ph:'06 12 34 56 78'},{key:'adresse',label:'Adresse',ph:'12 rue de la Paix, Paris'}]
                  .map(({key,label,ph,type}) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      <input type={type??'text'} value={form[key as keyof ProspectForm]}
                        onChange={e => setForm({...form,[key]:e.target.value})}
                        placeholder={ph}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"/>
                    </div>
                  ))}

                <div className="md:col-span-2 flex items-center gap-4 flex-wrap">
                  <button type="submit" disabled={loading}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white hover:opacity-90 disabled:opacity-60"
                    style={{ background:'#1A3C6E' }}>
                    <Plus className="w-4 h-4"/>
                    {loading ? 'Génération...' : "Générer le code d'accès"}
                  </button>
                  {generatedCode && (
                    <div className="flex items-center gap-3 px-4 py-2 rounded-xl"
                      style={{ background:'#F0FDF4', border:'1px solid #BBF7D0' }}>
                      <code className="font-mono font-bold text-sm" style={{ color:'#166534' }}>{generatedCode}</code>
                      <button type="button" onClick={() => handleCopy(generatedCode)}>
                        {copied ? <Check className="w-4 h-4 text-green-600"/> : <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600"/>}
                      </button>
                    </div>
                  )}
                </div>
                {error && <div className="md:col-span-2 p-3 rounded-lg text-sm" style={{ background:'#FFF5F5', color:'#C62828', border:'1px solid #FEE2E2' }}>{error}</div>}
                {success && <div className="md:col-span-2 p-3 rounded-lg text-sm" style={{ background:'#F0FDF4', color:'#166534', border:'1px solid #BBF7D0' }}>{success}</div>}
              </form>
            </div>
          )}
        </div>

        {/* Liste */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" style={{ color:'#1A3C6E' }} />
              <span className="font-semibold text-gray-800">Prospects <span className="text-sm font-normal text-gray-400">({prospects.length})</span></span>
            </div>
            <div className="flex gap-2">
              <button onClick={load} disabled={loadingP}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:text-gray-700">
                <RefreshCw className={`w-3 h-3 ${loadingP?'animate-spin':''}`}/> Actualiser
              </button>
              <button onClick={exportCSV} disabled={prospects.length===0}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-40"
                style={{ background:'#1A3C6E' }}>
                <Download className="w-3 h-3"/> CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loadingP ? (
              <div className="py-12 text-center text-gray-400 text-sm">Chargement...</div>
            ) : prospects.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">Aucun prospect.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background:'#F8FAFC' }}>
                    {['Nom','Salon','Email','Code','Créé le','Statut','Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((p, i) => (
                    <>
                      <tr key={p.id} className={i%2===0?'':'bg-gray-50/50'}>
                        <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{p.prenom} {p.nom}</td>
                        <td className="px-4 py-3 text-gray-600">{p.salon}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{p.email}</td>
                        <td className="px-4 py-3">
                          <code className="font-mono text-xs px-2 py-1 rounded" style={{ background:'#F0F4FF', color:'#1A3C6E' }}>{p.code}</code>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded-full text-xs font-medium"
                            style={p.first_login_at ? { background:'#F0FDF4', color:'#166534' } : { background:'#FFF7ED', color:'#9A3412' }}>
                            {p.first_login_at ? 'Actif' : 'Jamais connecté'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleCopy(p.code)} title="Copier le code"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <Copy className="w-3.5 h-3.5"/>
                            </button>
                            <button onClick={() => startEdit(p)} title="Modifier"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-50 transition-colors">
                              <Edit2 className="w-3.5 h-3.5"/>
                            </button>
                            <button onClick={() => handleDelete(p)} title="Supprimer"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-3.5 h-3.5"/>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Panneau d'édition inline */}
                      {editingId === p.id && (
                        <tr key={`edit-${p.id}`}>
                          <td colSpan={7} className="px-4 pb-4 pt-0">
                            <div className="rounded-xl p-4" style={{ background:'#F0F7FF', border:'1px solid #DBEAFE' }}>
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-semibold text-sm" style={{ color:'#1D4ED8' }}>✏️ Modifier le prospect</span>
                                <button onClick={() => setEditingId(null)}>
                                  <X className="w-4 h-4 text-gray-400 hover:text-gray-600"/>
                                </button>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {[{k:'prenom',l:'Prénom'},{k:'nom',l:'Nom'},{k:'salon',l:'Salon'},{k:'email',l:'Email'},{k:'telephone',l:'Téléphone'}].map(({k,l}) => (
                                  <div key={k}>
                                    <label className="block text-xs text-gray-500 mb-1">{l}</label>
                                    <input type="text" value={editForm[k as keyof typeof editForm] ?? ''}
                                      onChange={e => setEditForm({...editForm,[k]:e.target.value})}
                                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2"/>
                                  </div>
                                ))}
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Code d'accès</label>
                                  <div className="flex gap-1.5">
                                    <input type="text" value={editForm.code ?? ''}
                                      onChange={e => setEditForm({...editForm, code: e.target.value.toUpperCase()})}
                                      className="flex-1 px-3 py-1.5 text-sm font-mono border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2"/>
                                    <button type="button" onClick={handleRegenCode}
                                      title="Générer un nouveau code"
                                      className="p-1.5 rounded-lg text-white"
                                      style={{ background:'#1A3C6E' }}>
                                      <RotateCcw className="w-3.5 h-3.5"/>
                                    </button>
                                  </div>
                                  <p className="text-xs text-orange-500 mt-0.5">⚠ Changer le code réinitialise le mot de passe</p>
                                </div>
                              </div>
                              <div className="flex gap-2 mt-3">
                                <button onClick={handleSaveEdit} disabled={editLoading}
                                  className="px-4 py-2 rounded-lg font-semibold text-white text-sm hover:opacity-90 disabled:opacity-60"
                                  style={{ background:'#1A3C6E' }}>
                                  {editLoading ? 'Sauvegarde...' : '✓ Enregistrer'}
                                </button>
                                <button onClick={() => setEditingId(null)}
                                  className="px-4 py-2 rounded-lg text-sm text-gray-500 border border-gray-200 hover:bg-gray-50">
                                  Annuler
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
